import { AutogenLayoutDriverManagementMultiProfile } from "./autogen/AutogenLayoutDriverManagementMultiProfile";
import { LMEUserSettings } from "@mcleod/general";
import { MultiProfilePlanningTabs } from "../MultiProfilePlanningTabs";
import { Component, DataSourceRemoteDataChangeEvent, DataSourceSubscriberErrorEvent, PanelProps, SelectionEvent, SelectionListener, Snackbar, SnackbarOptions, Tab, Toast } from "@mcleod/components";
import { ModelDriverManagerProfile, RowDriverManagerProfile } from "../../../planning/src/models/ModelDriverManagerProfile";
import { LogManager, ModelRow } from "@mcleod/core";
import { DriverManagement } from "./DriverManagement";

const log = LogManager.getLogger("lme.display.planning.DriverManagementMultiProfile");

export class DriverManagementMultiProfile extends AutogenLayoutDriverManagementMultiProfile {
    private userProfiles: string[] = [];
    private _profile: RowDriverManagerProfile;
    private showingErrorToast: boolean;

    override onLoad() {
        if (LMEUserSettings.get()?.driver_manager_profile != null)
            this.userProfiles.push(LMEUserSettings.get().driver_manager_profile);
        if (LMEUserSettings.get()?.driver_manager_profile2 != null)
            this.userProfiles.push(LMEUserSettings.get().driver_manager_profile2);
        if (LMEUserSettings.get()?.driver_manager_profile3 != null)
            this.userProfiles.push(LMEUserSettings.get().driver_manager_profile3);
        if (LMEUserSettings.get()?.driver_manager_profile4 != null)
            this.userProfiles.push(LMEUserSettings.get().driver_manager_profile4);

        const multiProfile = new MultiProfilePlanningTabs({
            tabset: this.tabsetDriverManagementProfiles,
            profileDataSource: this.sourceDriverManagerProfile,
            mainLayoutName: "lme/dispatch/planning/drivermgmt/DriverManagement",
            advancedFilterLayout: "lme/dispatch/planning/drivermgmt/DriverManagerProfileFilter",
            startingProfiles: this.userProfiles,
            profileDescrFieldName: "id"
        });

        if (this.userProfiles.length > 0) {
            this.loadProfile(this.userProfiles[0]).then(() => {
                const profile = this.profile as RowDriverManagerProfile;
                this.getSubscription(profile);
            });
        } else {
            const selectionListener:SelectionListener = (event: SelectionEvent) => {
                this.loadProfile(multiProfile.userProfiles[0]).then(() => {
                    const profile = this.profile as RowDriverManagerProfile;
                    this.getSubscription(profile);
                });
            }
            selectionListener.runsOnce = true;
            this.tabsetDriverManagementProfiles.addAfterTabSelectionListener(selectionListener);
        }
    }

    async loadProfile(profile?: string | ModelRow) {
        if (profile instanceof ModelRow) {
            await this.setProfile(profile);
            return;
        }
        await this.loadProfileFromString(profile ?? LMEUserSettings.get()?.driver_manager_profile);
    }

    public get profile(): RowDriverManagerProfile {
        return this._profile;
    }

    private async setProfile(value: RowDriverManagerProfile) {
        if (value != null && value != this._profile) {
            this._profile = value;
        }
    }

    private async loadProfileFromString(profileId: string) {
        if (profileId == null) {
            const options: Partial<SnackbarOptions> = { persist: true, id: "noDriverManagementProfileDefined" };
            Snackbar.showSnackbar("You do not have a default driver management profile defined. "
                + "Add a default profile to your preferences so your orders will automatically "
                + "display or you can select a profile above to get started.", options);
        }
        else {
            const profile = await new ModelDriverManagerProfile().searchSingle({id: profileId});
            if (profile == null) {
                const options: Partial<SnackbarOptions> = { persist: true, id: "driverManagementProfileNotFound" };
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", options);
            }
            else
                this.setProfile(profile);
        }
    }

    // Override the main router's padding to get the vertical profile tabset to be flush with the edge of the screen
    getRouterProps(): Partial<PanelProps> {
        return { padding: 0 };
    }

    private async getSubscription(profile: RowDriverManagerProfile) {
        this.sourceDriverManager.search({profile_row: profile, is_dummy: true}).then(() => this.sourceDriverManager.clear());
    }

    handleRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        event.preventDefault();
        this.tabsetDriverManagementProfiles.components.forEach((tab: Tab, index) => {
            tab.components.forEach((component: Component) => {
                if (component instanceof DriverManagement) {
                    const dataSource = component.sourceDriverManager
                    dataSource.handleDataChange({ ...event.change });
                }
            })
        })
    }

    onSubscriberError(event: DataSourceSubscriberErrorEvent) {
        log.error(event.error);
        if (!this.showingErrorToast) {
            this.showingErrorToast = true;
            Toast.showToast("There was a problem keeping this page up-to-date real time.  Try refreshing the page.", null, {
                persist: true,
                onDismiss: () => this.showingErrorToast = false
            });
        }
    }
}
